import { offsetLabel, timezoneAbbreviation, timezoneCountry, utcOffset } from "@visioni/a3s-dates";
/**
 * Which timezone a Patriot tools list reads its time range in.
 *
 * The zone that matters is the camera's — "what happened at 02:00" means
 * 02:00 where the camera stands — so a selected site's zone is the default,
 * and the viewer's own only when no site is selected. Either can be
 * overridden with any zone.
 *
 * The URL always carries the zone in use (see `RawActivationFilters`), and
 * whether that zone is "the default" is worked out by comparing it with the
 * default rather than stored: a link opened by a viewer elsewhere then shows
 * the sender's zone as an explicit choice, which is true for that viewer.
 */

export interface DefaultTimeZone {
  readonly source: "site" | "user";
  readonly timeZone: string;
  /** A site is selected but has no zone recorded, so the viewer's stands in. */
  readonly siteHasNoTimeZone?: true;
}

/**
 * @param siteTimeZone The selected site's zone; `null` for a site with none
 *   recorded, absent when no site is selected.
 */
export function defaultTimeZone({
  siteTimeZone,
  viewerTimeZone,
}: {
  readonly siteTimeZone?: string | null;
  readonly viewerTimeZone: string;
}): DefaultTimeZone {
  if (siteTimeZone === undefined) return { source: "user", timeZone: viewerTimeZone };
  if (siteTimeZone === null) return { source: "user", timeZone: viewerTimeZone, siteHasNoTimeZone: true };
  return { source: "site", timeZone: siteTimeZone };
}

/**
 * The zone to use once the site changes: a zone left on the old default
 * follows the new one, and an explicit choice stays put. `undefined` when the
 * new default is not yet known, for the page to fill in once it is.
 */
export function timeZoneAfterSiteChange(
  current: string,
  previousDefault: string | undefined,
  nextDefault: string | undefined,
): string | undefined {
  return current === previousDefault ? nextDefault : current;
}

export type TimeZoneGroup = "Suggested" | "All time zones";

export interface TimeZoneOption {
  readonly timeZone: string;
  readonly group: TimeZoneGroup;
  /** As the picker shows it — see {@link timeZoneLabel}. */
  readonly label: string;
}

/** The fleet's own regions, suggested above the full list. New Zealand's zones sit under `Pacific/`. */
const isSuggested = (timeZone: string) =>
  timeZone.startsWith("Australia/") || ["Pacific/Auckland", "Pacific/Chatham", "NZ", "NZ-CHAT"].includes(timeZone);

/** Not in the runtime's zone list, but what device logs and Patriot's own timestamps are compared against. */
const UTC = "UTC";

/**
 * The picker's zones: Australia and New Zealand suggested first, then every
 * zone — those two included — with UTC at the top. Each part is alphabetical
 * by label, so places read in order rather than by IANA region.
 *
 * @param known The runtime's zone list (`Intl.supportedValuesOf("timeZone")`).
 * @param inUse Zones the page already uses — the active one, the site's, the
 *   viewer's. Added when `known` leaves them out, as it does aliases, so the
 *   picker can always show what is applied.
 * @param at When zones are labelled for, so offsets are those of the filtered date.
 */
export function timeZoneOptions(known: readonly string[], inUse: readonly string[], at: Date): TimeZoneOption[] {
  const labelled = [...new Set([...known, ...inUse])]
    .filter((timeZone) => timeZone !== UTC)
    .map((timeZone) => ({ timeZone, label: timeZoneLabel(timeZone, at) }))
    .sort((a, b) => a.label.localeCompare(b.label, "en"));
  return [
    ...labelled.filter((zone) => isSuggested(zone.timeZone)).map((zone) => ({ ...zone, group: "Suggested" as const })),
    { timeZone: UTC, label: timeZoneLabel(UTC, at), group: "All time zones" },
    ...labelled.map((zone) => ({ ...zone, group: "All time zones" as const })),
  ];
}

/** The place an IANA id names, as people write it: `Australia/Lord_Howe` is Lord Howe. */
const placeOf = (timeZone: string) => (timeZone.split("/").pop() ?? timeZone).replaceAll("_", " ");

/**
 * How a zone reads in the picker — in the list and once selected — as at
 * `at`, so a zone that observes daylight saving shows the offset of the day
 * being filtered: `Auckland, New Zealand — UTC+13:00`.
 *
 * Led by the place and its country, which people recognise, with the offset
 * in full. A zone the runtime does not know shows as its id; UTC as "UTC".
 */
export function timeZoneLabel(timeZone: string, at: Date): string {
  const offset = utcOffset(timeZone, at);
  if (offset === undefined) return timeZone;
  if (timeZone === UTC) return UTC;
  const place = placeOf(timeZone);
  const country = timezoneCountry(timeZone);
  return `${country === undefined ? place : `${place}, ${country}`} — ${offset}`;
}

/**
 * Whether a picker search matches a zone: by its id, its label, its
 * abbreviation (AWST), or its offset in either form.
 */
export function matchesTimeZone(timeZone: string, at: Date, query: string): boolean {
  const needle = query.trim().toLowerCase();
  return [timeZone, timeZoneLabel(timeZone, at), timezoneAbbreviation(timeZone, at), offsetLabel(timeZone, at)].some(
    (text) => text?.toLowerCase().includes(needle) === true,
  );
}
